/**
 * VacationCalendarView
 * Renders day/week/month/quarter views for vacations.
 */
import React from 'react';
import {
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, isToday, addDays, addMonths, getDay, getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const statusBadgeStyle = (status) => {
  const styles = {
    pending: 'status-pending',
    approved: 'status-approved',
    rejected: 'status-rejected',
    cancelled: 'status-cancelled',
  };
  const labels = {
    pending: 'Ausstehend', approved: 'Genehmigt', rejected: 'Abgelehnt', cancelled: 'Storniert',
  };
  return <Badge className={styles[status] || ''}>{labels[status] || status}</Badge>;
};

const DragHint = () => (
  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
    <CalendarIcon className="h-3 w-3" />
    Tipp: Über Tage ziehen, um Urlaub direkt einzutragen
  </p>
);

const DayView = ({ currentDate, getVacationsForDay, getHolidayForDay, getSchulferienForDay }) => {
  const dayVacations = getVacationsForDay(currentDate);
  const dayFerien = getSchulferienForDay(currentDate);
  const holiday = getHolidayForDay(currentDate);
  return (
    <div className="space-y-4">
      <div className={`relative p-4 pb-6 rounded-lg ${isToday(currentDate) ? 'bg-primary/10 border-2 border-primary' : 'bg-secondary/30'}`}>
        <h3 className="font-bold text-lg mb-4">
          {format(currentDate, 'EEEE', { locale: de })}
          <span className="text-muted-foreground font-normal ml-2">
            {format(currentDate, 'dd.MM.yyyy', { locale: de })}
          </span>
        </h3>
        {holiday && (
          <div className="mb-3 px-2 py-1 rounded bg-purple-100 dark:bg-purple-800/40 text-purple-800 dark:text-purple-200 text-sm font-medium inline-block">
            {holiday.name}
          </div>
        )}
        {dayFerien.length > 0 && (
          <div className="mb-3 px-2 py-1 rounded bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-200 text-sm inline-block">
            {dayFerien.map(f => `${f.name}${f.bundesland ? ` (${f.bundesland_name || f.bundesland})` : ''}`).join(', ')}
          </div>
        )}
        {dayVacations.length > 0 ? (
          <div className="space-y-2">
            {dayVacations.map((vacation) => (
              <div
                key={vacation.id}
                className={`p-3 rounded-lg ${
                  vacation.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500' :
                  vacation.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-500' :
                  'bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{vacation.employee_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {vacation.vacation_type} • {vacation.start_date} bis {vacation.end_date}
                    </p>
                  </div>
                  {statusBadgeStyle(vacation.status)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">Keine Urlaube an diesem Tag</p>
        )}
        {dayFerien.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-amber-300 dark:bg-amber-600 rounded-b-lg" aria-hidden="true" />
        )}
      </div>
    </div>
  );
};

const WeekView = ({ currentDate, getVacationsForDay, getHolidayForDay, getSchulferienForDay, isDayInDragRange, handleDragStart, handleDragEnter }) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ferienByDay = days.map(d => getSchulferienForDay(d));
  return (
    <>
      <DragHint />
      <div className="grid grid-cols-7 gap-2 select-none">
        {days.map((day, i) => {
          const dayVacations = getVacationsForDay(day);
          const isCurrentDay = isToday(day);
          const isWeekend = getDay(day) === 0 || getDay(day) === 6;
          const isPast = day < today;
          const inDragRange = isDayInDragRange(day);
          const holiday = getHolidayForDay(day);
          const dayFerien = ferienByDay[i];
          const hasFerien = dayFerien.length > 0;
          const prevFerien = i > 0 ? ferienByDay[i - 1] : [];
          const nextFerien = i < 6 ? ferienByDay[i + 1] : [];
          const connectsLeft = hasFerien && prevFerien.some(pf => dayFerien.some(f => f.id === pf.id));
          const connectsRight = hasFerien && nextFerien.some(nf => dayFerien.some(f => f.id === nf.id));
          return (
            <div
              key={day.toISOString()}
              className={`relative min-h-[200px] p-2 rounded-lg border ${
                inDragRange ? 'ring-2 ring-primary bg-primary/15' :
                isCurrentDay ? 'border-primary bg-primary/5' :
                holiday ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200' :
                isWeekend ? 'bg-secondary/20 border-border/40' : 'border-border/40'
              } ${'cursor-crosshair'}`}
              onMouseDown={(e) => { e.preventDefault(); handleDragStart(day); }}
              onMouseEnter={() => handleDragEnter(day)}
              title={hasFerien ? dayFerien.map(f => `${f.name}${f.bundesland ? ` (${f.bundesland_name || f.bundesland})` : ''}`).join(', ') : undefined}
            >
              <div className={`text-center mb-2 pb-2 border-b border-border/40 ${isCurrentDay ? 'text-primary' : ''}`}>
                <div className="text-xs text-muted-foreground">{format(day, 'EEE', { locale: de })}</div>
                <div className={`text-lg font-bold ${isCurrentDay ? 'bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto' : ''}`}>
                  {format(day, 'd')}
                </div>
              </div>
              {holiday && (
                <div className="text-xs px-1 py-0.5 mb-1 rounded bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 truncate font-medium" title={holiday.name}>
                  {holiday.name}
                </div>
              )}
              <div className="space-y-1">
                {dayVacations.slice(0, 4).map((vacation) => (
                  <div
                    key={vacation.id}
                    className={`text-xs p-1 rounded truncate ${
                      vacation.status === 'approved' ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200' :
                      vacation.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200' :
                      'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                    }`}
                    title={`${vacation.employee_name} - ${vacation.vacation_type}`}
                  >
                    {vacation.employee_name?.split(' ')[0]}
                  </div>
                ))}
                {dayVacations.length > 4 && (
                  <div className="text-xs text-muted-foreground text-center">+{dayVacations.length - 4} mehr</div>
                )}
              </div>
              {hasFerien && (
                <span
                  className={`absolute bottom-0 h-1.5 bg-amber-300 dark:bg-amber-600 ${
                    connectsLeft ? '-left-2' : 'left-1 rounded-l-sm'
                  } ${
                    connectsRight ? '-right-2' : 'right-1 rounded-r-sm'
                  }`}
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

const MonthView = ({ currentDate, getVacationsForDay, getHolidayForDay, getSchulferienForDay, isDayInDragRange, handleDragStart, handleDragEnter }) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay();
  const paddingDays = startDay === 0 ? 6 : startDay - 1;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const weeks = [];
  let currentWeek = { kw: null, days: Array(paddingDays).fill(null) };
  daysInMonth.forEach((day) => {
    const kw = getISOWeek(day);
    const dayOfWeek = getDay(day);
    const mondayBased = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    if (mondayBased === 0 && currentWeek.days.length > 0) {
      weeks.push(currentWeek);
      currentWeek = { kw, days: [] };
    }
    if (!currentWeek.kw) currentWeek.kw = kw;
    currentWeek.days.push(day);
  });
  if (currentWeek.days.length > 0) {
    while (currentWeek.days.length < 7) currentWeek.days.push(null);
    weeks.push(currentWeek);
  }

  return (
    <>
      <DragHint />
      <div className="grid grid-cols-7 sm:grid-cols-[40px_repeat(7,1fr)] mb-2">
        <div className="hidden sm:block text-center text-xs font-medium text-muted-foreground py-2">KW</div>
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
          <div key={day} className="text-center text-xs sm:text-sm font-medium text-muted-foreground py-2">{day}</div>
        ))}
      </div>
      <div className="select-none">
        {weeks.map((week) => {
          const ferienByDayInWeek = week.days.map(d => (d ? getSchulferienForDay(d) : []));
          return (
          <div key={`week-${week.kw}`} className="grid grid-cols-7 sm:grid-cols-[40px_repeat(7,1fr)] gap-0.5 sm:gap-1 mb-1">
            <div className="hidden sm:flex items-start justify-center pt-2 text-xs font-medium text-muted-foreground">{week.kw}</div>
            {week.days.map((day, dayIdx) => {
              if (!day) return <div key={`empty-${dayIdx}`} className="min-h-[60px] sm:min-h-[80px]" />;
              const dayVacations = getVacationsForDay(day);
              const isCurrentDay = isToday(day);
              const isPast = day < today;
              const inDragRange = isDayInDragRange(day);
              const holiday = getHolidayForDay(day);
              const dayFerien = ferienByDayInWeek[dayIdx];
              const hasFerien = dayFerien.length > 0;
              const prevFerien = dayIdx > 0 ? ferienByDayInWeek[dayIdx - 1] : [];
              const nextFerien = dayIdx < 6 ? ferienByDayInWeek[dayIdx + 1] : [];
              const connectsLeft = hasFerien && prevFerien.some(pf => dayFerien.some(f => f.id === pf.id));
              const connectsRight = hasFerien && nextFerien.some(nf => dayFerien.some(f => f.id === nf.id));
              return (
                <div
                  key={day.toISOString()}
                  className={`calendar-cell relative ${isCurrentDay ? 'today' : ''} ${
                    inDragRange ? 'ring-2 ring-primary bg-primary/15' : ''
                  } ${holiday ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' :
                    hasFerien && !inDragRange ? 'bg-amber-50/60 dark:bg-amber-900/10 border-amber-200/50 dark:border-amber-800/30' : ''} ${
                    'cursor-crosshair'
                  }`}
                  onMouseDown={(e) => { e.preventDefault(); handleDragStart(day); }}
                  onMouseEnter={() => handleDragEnter(day)}
                  data-testid={`day-${format(day, 'yyyy-MM-dd')}`}
                  title={hasFerien ? dayFerien.map(f => `${f.name}${f.bundesland ? ` (${f.bundesland_name || f.bundesland})` : ''}`).join(', ') : undefined}
                >
                  <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-primary' : ''}`}>{format(day, 'd')}</div>
                  {holiday && (
                    <div className="text-[10px] leading-tight px-0.5 py-0.5 mb-0.5 rounded bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 truncate font-medium" title={holiday.name}>
                      {holiday.name}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {dayVacations.slice(0, holiday ? 1 : 2).map((vacation) => (
                      <div
                        key={vacation.id}
                        className={`text-xs px-1 py-0.5 rounded truncate ${
                          vacation.status === 'approved' ? 'bg-green-100 text-green-800' :
                          vacation.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}
                        title={vacation.employee_name}
                      >
                        {vacation.employee_name?.split(' ')[0]}
                      </div>
                    ))}
                    {dayVacations.length > (holiday ? 1 : 2) && (
                      <div className="text-xs text-muted-foreground">+{dayVacations.length - (holiday ? 1 : 2)} mehr</div>
                    )}
                  </div>
                  {hasFerien && (
                    <span
                      className={`absolute bottom-0 h-1.5 bg-amber-300 dark:bg-amber-600 ${
                        connectsLeft ? '-left-1 sm:-left-2' : 'left-1 rounded-l-sm'
                      } ${
                        connectsRight ? '-right-1 sm:-right-2' : 'right-1 rounded-r-sm'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </div>
              );
            })}
          </div>
          );
        })}
      </div>
    </>
  );
};

const QuarterView = ({ currentDate, getVacationsForDay, getHolidayForDay, getSchulferienForDay }) => {
  const months = [0, 1, 2].map(i => addMonths(currentDate, i));
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {months.map((month) => {
        const monthStart = startOfMonth(month);
        const monthEnd = endOfMonth(month);
        const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
        const startDay = monthStart.getDay();
        const paddingDays = startDay === 0 ? 6 : startDay - 1;
        // Pre-compute Ferien per day for connection logic
        const ferienByDay = daysInMonth.map(d => getSchulferienForDay(d));
        return (
          <div key={month.toISOString()} className="border border-border/40 rounded-lg p-3">
            <h4 className="font-bold text-center mb-3 pb-2 border-b border-border/40">
              {format(month, 'MMMM yyyy', { locale: de })}
            </h4>
            <div className="grid grid-cols-7 mb-1">
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                <div key={day} className="text-center text-xs text-muted-foreground">{day.charAt(0)}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {Array.from({ length: paddingDays }).map((_, i) => <div key={`pad-${i}`} className="h-7" />)}
              {daysInMonth.map((day, dayIdx) => {
                const dayVacations = getVacationsForDay(day);
                const isCurrentDay = isToday(day);
                const hasApproved = dayVacations.some(v => v.status === 'approved');
                const hasPending = dayVacations.some(v => v.status === 'pending');
                const holiday = getHolidayForDay(day);

                const dayFerien = ferienByDay[dayIdx];
                const hasFerien = dayFerien.length > 0;
                const colIdx = (paddingDays + dayIdx) % 7;
                const prevFerien = colIdx > 0 && dayIdx > 0 ? ferienByDay[dayIdx - 1] : [];
                const nextFerien = colIdx < 6 && dayIdx < daysInMonth.length - 1 ? ferienByDay[dayIdx + 1] : [];
                const connectsLeft = hasFerien && prevFerien.some(pf => dayFerien.some(f => f.id === pf.id));
                const connectsRight = hasFerien && nextFerien.some(nf => dayFerien.some(f => f.id === nf.id));

                let dayClass = '';
                if (isCurrentDay) dayClass = 'bg-primary text-primary-foreground font-bold';
                else if (holiday) dayClass = 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 font-medium';
                else if (hasApproved) dayClass = 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200';
                else if (hasPending) dayClass = 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200';

                let dayTitle;
                if (holiday) dayTitle = holiday.name;
                else if (dayVacations.length > 0) dayTitle = dayVacations.map(v => v.employee_name).join(', ');
                else if (hasFerien) dayTitle = dayFerien.map(f => `${f.name}${f.bundesland ? ` (${f.bundesland_name || f.bundesland})` : ''}`).join(', ');

                return (
                  <div
                    key={day.toISOString()}
                    className="relative h-7 flex items-center justify-center text-xs"
                    title={dayTitle}
                  >
                    <span className={`relative z-10 w-5 h-5 flex items-center justify-center rounded ${dayClass}`}>
                      {format(day, 'd')}
                    </span>
                    {hasFerien && !holiday && !isCurrentDay && !hasApproved && !hasPending && (
                      <span
                        className={`absolute bottom-0 h-1.5 bg-amber-300 dark:bg-amber-600 ${
                          connectsLeft ? 'left-0' : 'left-0.5 rounded-l-sm'
                        } ${
                          connectsRight ? 'right-0' : 'right-0.5 rounded-r-sm'
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function VacationCalendarView({ viewMode, ...props }) {
  if (viewMode === 'day') return <DayView {...props} />;
  if (viewMode === 'week') return <WeekView {...props} />;
  if (viewMode === 'month') return <MonthView {...props} />;
  if (viewMode === 'quarter') return <QuarterView {...props} />;
  return null;
}

export { statusBadgeStyle as getStatusBadge };
